'use client';

import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import GenericAccordion from '@/components/organisms/generic-accordion/generic-accordion';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import BookingFlowModuleList, {
  BookingFlowModule,
} from '@/components/utilities/content-modules/booking-flow-module-list/booking-flow-module-list';
import { Module } from '@/components/utilities/content-modules/module-list/module-list';
import Row from '@/components/utilities/row/row';
import {
  trackPageLoadEvent,
  trackViewEvent,
} from '@/integrations/tracking/google-tag-manager/scripts';
import { tealiumView } from '@/integrations/tracking/tealium/scripts';
import styles from './my-trip-products.module.scss';

export interface MyTripProductProps {
  /** An array of the content modules to show */
  contentModules: Module[];
  /** The link to the my trip page */
  myTripPageLink: Content.Components['LinkDto'];
  /** Personalization data for the user */
  personalizationData?: Content.Properties.PersonalizationData;
  /** An array of the content modules to show */
  sections: Content.Components['BookingFlowSectionDto'][];
  /** Tracking data for the page */
  trackingData?: Content.Properties.TrackingData;
}

const MyTripProduct = ({
  contentModules,
  myTripPageLink,
  personalizationData,
  sections,
  trackingData,
}: MyTripProductProps) => {
  const pathname = usePathname();
  const [expandedSections, setExpandedSections] = useState<
    Record<number, boolean>
  >({});
  const hasProcessedHashRef = useRef(false);

  useEffect(() => {
    trackPageLoadEvent({ content_group: 'my_trip_products' });

    personalizationData && tealiumView(personalizationData);

    if (!trackingData?.gtmDataLayer) return;

    trackViewEvent(trackingData.gtmDataLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle anchor links from URL
  useEffect(() => {
    if (!sections || hasProcessedHashRef.current) return;

    const hash = pathname!.split('#')[1];

    if (!hash) return;

    let targetSectionIndex = -1;

    sections.forEach((section, sectionIndex) => {
      const hasMatchingModule = section.contentModules?.some(
        module => module.settings?.anchorLinkSettings?.anchorId === hash,
      );

      if (hasMatchingModule) {
        targetSectionIndex = sectionIndex;
      }
    });

    if (
      targetSectionIndex !== -1 &&
      sections[targetSectionIndex].accordionView
    ) {
      setExpandedSections(prev => ({
        ...prev,
        [targetSectionIndex]: true,
      }));

      hasProcessedHashRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, pathname]);

  const getSectionInitialExpanded = (sectionIndex: number) => {
    if (expandedSections[sectionIndex] !== undefined) {
      return expandedSections[sectionIndex];
    }

    return !sections?.[sectionIndex]?.foldAccordionOnLoad;
  };

  return (
    <div className={styles.component}>
      <Container>
        <Row>
          <Column
            className={styles.innerWrapper}
            offset={{ laptop: 1 }}
            width={{ laptop: 7, tablet: 8 }}
          >
            {sections.length > 0 && (
              <div className={styles.sections}>
                {sections.map((section, sectionIndex) => {
                  if (section.accordionView) {
                    return (
                      <GenericAccordion
                        key={`${sectionIndex}-${getSectionInitialExpanded(sectionIndex)}`}
                        additionalInfo={section.accordionText}
                        className={styles.section}
                        headline={section.headline}
                        headlineTagStyle="headingMedium"
                        initialExpanded={getSectionInitialExpanded(
                          sectionIndex,
                        )}
                        showBottomBorder
                      >
                        <div className={styles.sectionAccordionContent}>
                          {section.description && (
                            <Typography
                              className={styles.description}
                              tagStyle="bodyMedium"
                            >
                              {section.description}
                            </Typography>
                          )}

                          <div
                            className={classNames(styles.modules, {
                              [styles.gridView]: section.gridView,
                            })}
                          >
                            <BookingFlowModuleList
                              key={sectionIndex}
                              contentModules={
                                section.contentModules as unknown as BookingFlowModule[]
                              }
                            />
                          </div>
                        </div>
                      </GenericAccordion>
                    );
                  }

                  return (
                    <div key={sectionIndex} className={styles.section}>
                      {(section.headline || section.description) && (
                        <div className={styles.sectionHeader}>
                          <Typography
                            className={styles.headline}
                            tagStyle="headingMedium"
                          >
                            {section.headline}
                          </Typography>

                          <Typography
                            className={styles.description}
                            tagStyle="bodyMedium"
                          >
                            {section.description}
                          </Typography>
                        </div>
                      )}

                      <div
                        className={classNames(styles.modules, {
                          [styles.gridView]: section.gridView,
                        })}
                      >
                        <BookingFlowModuleList
                          key={sectionIndex}
                          contentModules={
                            section.contentModules as unknown as BookingFlowModule[]
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className={styles.buttonWrapper}>
              <Button
                className={styles.button}
                text={myTripPageLink.text}
                url={myTripPageLink.url}
                variant="primary"
              />
            </div>
          </Column>

          <Column width={{ laptop: 3, tablet: 4 }}>
            <div className={styles.summary}>
              <BookingFlowModuleList
                contentModules={
                  contentModules as unknown as BookingFlowModule[]
                }
              />
            </div>
          </Column>
        </Row>
      </Container>
    </div>
  );
};

MyTripProduct.displayName = 'MyTripProduct';

export default MyTripProduct;
